import { ICartItem } from '../../../projects/shopping-cart/cart-bridger-pay/interfaces';
import {
    IUpsellExcludedProduct,
    IUpsellProgressBar,
    IUpsellRule,
    IUpsellTypeResult,
    UpsellReferrer
} from '../interfaces';
import { ProductDepartmentEnum } from '../../../projects/products/enums/product-department-enum';
import { round2DecimalPlaces } from '../../math-provider';
import { plural } from '../../../projects/shopping-cart/cart-bridger-pay/ui/free-plugin/free-plugin-box';
import ProductPriceFormat from '../../../projects/products/product-price/product-price-format';
import React from 'react';

/**
 * $ - fixed price for x plugins
 * Buy x number of plugins for $y amount
 */
export class UpsellType2 {

    private readonly _cartItems: ICartItem[];
    private readonly _upsellRules: IUpsellRule[];
    private _upsellExcludedProducts: IUpsellExcludedProduct[];
    private readonly _referrer: UpsellReferrer;
    private readonly _totalUpsellDiscount: number;

    private readonly _upsellType = 2;

    constructor(
        cartItems: ICartItem[],
        upsellRules: IUpsellRule[],
        upsellExcludedProducts: IUpsellExcludedProduct[],
        referrer: UpsellReferrer,
        totalUpsellDiscount: number
    ) {
        this._cartItems = cartItems;
        this._referrer = referrer;
        this._totalUpsellDiscount = totalUpsellDiscount;

        this._upsellRules = [...upsellRules];
        this._upsellRules.sort((rule1, rule2) => rule2.pluginQuantity - rule1.pluginQuantity);

        this._upsellExcludedProducts = upsellExcludedProducts;
    }

    /**
     * "Add 1 plugin to get 2 plugins for $50"
     * "Add 3 plugins to get 5 plugins for $100"
     */
    public getUpsellTypeResult() : IUpsellTypeResult {

        try{
            const { rule, total, isAboveMaxTier, productsCount } = this.getNextActiveRule();
            if(!rule) {
                return {
                    isVisible: isAboveMaxTier,
                    isAboveMaxTier,
                    upsellType: this._upsellType,
                    productsCount,
                };
            }

            // "Add 1 plugin to get 2 plugins for $50"
            // "Add 3 plugins to get 5 plugins for $100"

            const totalQty = total;
            const productsQty = rule.pluginQuantity;
            const addQty = Math.abs(totalQty - productsQty);

            // console.log(`rule.pluginQuantity - totalQty = ${ rule.pluginQuantity } - ${ totalQty } = ${ addQty }`);

            const percent = totalQty * 100 / productsQty;
            const percentRounded = round2DecimalPlaces(percent);
            const discount = rule.value;

            const progressBar: IUpsellProgressBar = {
                min: 0,
                max: productsQty,
                value: addQty,
                percent: percentRounded,
                ariaText: `Add $${ addQty } ${ plural('plugin', addQty) } to get $${ productsQty } ${ plural('plugin', productsQty) } for $${ discount }`,
                userValue: total,
            };

            const text = (
                <div>
                    Add { addQty } { plural('plugin', addQty) } to get <strong>{ productsQty } { plural('plugin', productsQty) } for <ProductPriceFormat price={ discount } /></strong>
                </div>
            );

            return {
                isVisible: true,
                add: addQty,
                productsQty,
                discount,
                progressBar,
                isAboveMaxTier,
                upsellType: this._upsellType,
                text,
                productsCount,
            }
        }
        catch(error) {
            console.error(error);
            return {
                isVisible: false,
                isAboveMaxTier: false,
                upsellType: this._upsellType,
                productsCount: 0,
            };
        }
    }

    private getNextActiveRule = () : {
        rule: IUpsellRule|null;
        total: number;
        isAboveMaxTier: boolean;
        productsCount: number;
    } => {
        const filteredCartItems = this.filterNonUpsellProducts();
        const productsCount = this.getProductsCount(filteredCartItems);

        if(productsCount <= 0) {
            return {
                rule: null,
                total: productsCount,
                isAboveMaxTier: false,
                productsCount,
            }
        }

        const hasUpsellRules = this._upsellRules.length > 0;
        const instantSavingsShown = this._totalUpsellDiscount > 0;

        if(this._referrer === UpsellReferrer.CartStep3) {
            return {
                rule: null,
                isAboveMaxTier: hasUpsellRules && instantSavingsShown,
                total: productsCount,
                productsCount,
            };
        }

        const nextActiveRule = this.getNextActiveRuleByProductsCount(productsCount);
        const isAboveMaxTier = !nextActiveRule && hasUpsellRules;

        return {
            rule: nextActiveRule,
            total: productsCount,
            isAboveMaxTier,
            productsCount,
        };
    };

    private getProductsCount = (filteredCartItems: ICartItem[]) : number => {
        let productsCount = 0;

        for (const upsellCartItem of filteredCartItems)
        {
            productsCount += upsellCartItem.skuUnits;
        }

        return productsCount;
    };

    private getNextActiveRuleByProductsCount = (productsCount: number) : IUpsellRule|null => {
        let selectedRule: IUpsellRule|null = null;

        for(const rule of this._upsellRules) {
            if (productsCount < rule.pluginQuantity)
            {
                selectedRule = rule;
            }
        }

        return selectedRule;
    };

    private filterNonUpsellProducts(): ICartItem[] {

        const filtered: ICartItem[] = [];

        for(const cartItem of this._cartItems) {

            if(!cartItem.isActive ||
                !cartItem.skuEnabled ||
                cartItem.skuPrice <= 0) continue;

            if(cartItem.skuDepartmentID !== ProductDepartmentEnum.Plugins) continue;

            const isExcluded = this._upsellExcludedProducts.find(item => item.skuid === cartItem.skuid);
            if(isExcluded) {
                // console.log(`${ cartItem.skuName } - excluded`);
                continue;
            }

            filtered.push(cartItem);
        }

        return filtered;
    }
}